import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Bell, FileText, Loader2, MessageSquare, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SectionErrorBoundary } from "@/components/common/SectionErrorBoundary";
import { useNotifications } from "@/hooks/useNotifications";

const typeConfig: Record<string, { icon: typeof FileText; color: string }> = {
  document: { icon: FileText, color: "bg-info/10 text-info" },
  deal: { icon: MessageSquare, color: "bg-primary/10 text-primary" },
  matter: { icon: TrendingUp, color: "bg-success/10 text-success" },
  billing: { icon: TrendingUp, color: "bg-warning/10 text-warning" },
  message: { icon: MessageSquare, color: "bg-primary/10 text-primary" },
  buyer_message: { icon: MessageSquare, color: "bg-client/10 text-client" },
  seller_message: { icon: MessageSquare, color: "bg-agent/10 text-agent" },
  system: { icon: Bell, color: "bg-muted text-muted-foreground" },
};

export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, isLoading, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const handleClick = async (notification: any) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    if (notification.link_to) {
      navigate(notification.link_to);
    }

    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <SectionErrorBoundary fallbackTitle="Notifications are unavailable right now.">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-foreground">Notifications</h4>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-destructive px-2.5 text-destructive-foreground">
                  {unreadCount}
                </Badge>
              )}
            </div>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => void markAllAsRead()}>
                Mark all read
              </Button>
            )}
          </div>

          <ScrollArea className="h-96">
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.slice(0, 10).map((notification) => {
                  const config = typeConfig[notification.type || "system"] || typeConfig.system;
                  const TypeIcon = config.icon;

                  return (
                    <div
                      key={notification.id}
                      className={`cursor-pointer p-4 transition-colors hover:bg-secondary/50 ${!notification.isRead ? "bg-primary/5" : ""}`}
                      onClick={() => void handleClick(notification)}
                    >
                      <div className="flex gap-3">
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${config.color}`}>
                          <TypeIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${!notification.isRead ? "text-foreground" : "text-muted-foreground"}`}>
                              {notification.title}
                            </p>
                            {!notification.isRead && (
                              <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                            {notification.message}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                          {!notification.isRead && (
                            <Button
                              variant="link"
                              size="sm"
                              className="mt-1 h-auto px-0"
                              onClick={(event) => {
                                event.stopPropagation();
                                void markAsRead(notification.id);
                              }}
                            >
                              Mark as read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <div className="border-t border-border p-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                navigate("/notifications");
                setIsOpen(false);
              }}
            >
              View All Notifications
            </Button>
          </div>
        </SectionErrorBoundary>
      </PopoverContent>
    </Popover>
  );
}
